/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
    ContentInfoCardKernel,
    InfoCardKernel,
    InfoCardKernelType,
    OfferInfoCardKernel,
    UnifiedCollectionInfoCardKernel,
    VodOfferInfoCardKernel,
} from 'client-code/actions';
import {
    ContentVodItemKernel,
    OfferVodItemKernel,
    UnifiedCollectionVodItemKernel,
    VodItem,
    VodItemKernelType,
    VodItemType,
} from 'client-code/vod-browse';

import {
    IInfoCard,
    IInfoCardProperty,
    InfoCardManager,
    InfoCardUserInputHandler,
} from '../infocard';

export class GalleryScreenInfoCardUserInputHandler extends InfoCardUserInputHandler {
    private mInfoCard: IInfoCard = InfoCardManager.getInstance();

    private horizontalKeyNavigationHelper(direction: 'left' | 'right'): void {
        const { activeItemIndex, itemList } = (this.mInfoCard.getContextData(
            GalleryScreenInfoCardUserInputHandler.name,
        ) || {}) as { activeItemIndex: number; itemList: VodItem[] };

        // Filter out folder items for navigation
        const navigableItems = itemList.filter(
            (item) => item.vodItemType.toLowerCase() !== VodItemType.FolderVodItem.toLowerCase(),
        );
        const currentNavigableIndex = navigableItems.indexOf(itemList[activeItemIndex]);
        const nextNavigableIndex =
            direction === 'left' ? currentNavigableIndex - 1 : currentNavigableIndex + 1;

        // Don't navigate beyond boundaries
        if (nextNavigableIndex < 0 || nextNavigableIndex >= navigableItems.length) return;

        const nextItem = navigableItems[nextNavigableIndex];
        const nextItemIndex = itemList.indexOf(nextItem);

        this.mInfoCard.hideActionButtons();
        this.mInfoCard.setInfoCardKernel(this.getInfoCardKernel(nextItem));
        this.mInfoCard.setContextData(GalleryScreenInfoCardUserInputHandler.name, {
            activeItemIndex: nextItemIndex,
            itemList,
        });
        this.mInfoCard.setProperties({
            hasItemsBefore: nextNavigableIndex > 0,
            hasItemsAfter: nextNavigableIndex < navigableItems.length - 1,
        });
    }

    /**
     * @override
     */
    public onKeyLeft(properties: IInfoCardProperty): void {
        if (!properties.hasItemsBefore) return;
        this.horizontalKeyNavigationHelper('left');
    }

    /**
     * @override
     */
    public onKeyRight(properties: IInfoCardProperty): void {
        if (!properties.hasItemsAfter) return;
        this.horizontalKeyNavigationHelper('right');
    }

    // eslint-disable-next-line class-methods-use-this
    public getInfoCardKernel(item: VodItem): InfoCardKernel {
        switch (item.vodItemType.toLowerCase()) {
            case VodItemType.EpisodeVodItem.toLowerCase(): {
                if (
                    item.vodItemKernel?.vodItemKernelType === VodItemKernelType.OfferVodItemKernel
                ) {
                    return {
                        infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                        offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                    } as OfferInfoCardKernel;
                }
                const { contentId, partnerId, collapsedVodItemId } =
                    item.vodItemKernel as ContentVodItemKernel;
                return {
                    infoCardKernelType: InfoCardKernelType.ContentInfoCardKernel,
                    contentId,
                    partnerId,
                    collapsedVodItemId,
                } as ContentInfoCardKernel;
            }

            case VodItemType.MovieVodItem.toLowerCase():
            case VodItemType.SpecialVodItem.toLowerCase():
                return {
                    infoCardKernelType: InfoCardKernelType.VodOfferInfoCardKernel,
                    offerId: (item.vodItemKernel as OfferVodItemKernel).offerId,
                } as VodOfferInfoCardKernel;

            default: {
                const { unifiedCollectionId, partnerId, collapsedVodItemId } =
                    item.vodItemKernel as UnifiedCollectionVodItemKernel;
                return {
                    infoCardKernelType: InfoCardKernelType.UnifiedCollectionInfoCardKernel,
                    unifiedCollectionId,
                    partnerId,
                    collapsedVodItemId,
                } as UnifiedCollectionInfoCardKernel;
            }
        }
    }
}
